/*
Fork addition (Stage D): tolerant blueprint text extraction.

Pasted blueprint strings arrive dirty: copied out of chat they gain
trailing blank lines, surrounding sentences, code fences, or lose their
====Name==== header line. The serializer's decoder wants the bare
encoded token, so before anything reaches it we try to pull the token
(and the blueprint name) out of whatever was actually pasted.

This file is pure string handling — it never changes the encoded format
(DAXFB<game><type>$ + base64url payload, see model/serializer.ts).
*/

//a whole line of payload: base64url characters plus optional padding
const TOKEN_LINE = /^[A-Za-z0-9_-]+={0,2}$/;
//====Name==== somewhere on a line (chat text may precede it on that line)
const NAME_LINE = /={2,}([^=\n]+?)={2,}\s*$/;

export type ExtractedBlueprint = {
    encodedBlueprint: string;
    blueprintName?: string;
};

function findBlueprintName(textBeforeMarker: string) {
    //the header line sits above the token; scan upwards so surrounding
    //chat lines further up don't win over the closest one
    const lines = textBeforeMarker.split(/\r?\n/);
    for(let i = lines.length - 1; i >= 0; --i) {
        const match = NAME_LINE.exec(lines[i].trim());
        if(match) {
            const name = match[1].trim();
            if(name)
                return name;
        }
    }
    return undefined;
}

//Finds the encoded blueprint anywhere in `rawText` and returns just the
//token (plus the name from a ====Name==== header line, if one precedes
//it). Returns undefined when no marker for this game is present, so the
//caller can fall back to the classic whole-text decode path.
export function extractEncodedBlueprint(rawText: string, gameHeader: string): ExtractedBlueprint | undefined {
    const escapedHeader = gameHeader.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const markerMatch = new RegExp(`DAXFB${escapedHeader}[CBJ]\\$`).exec(rawText);
    if(!markerMatch)
        return undefined;
    const markerIndex = markerMatch.index;
    const type = markerMatch[0].charAt(markerMatch[0].length - 2);
    const blueprintName = findBlueprintName(rawText.substring(0, markerIndex));
    if(type == 'J') {
        //JSON payloads are not token-shaped; hand the decoder the rest
        //of the text verbatim (it strips whitespace itself)
        return {
            encodedBlueprint: rawText.substring(markerIndex).trim(),
            blueprintName,
        };
    }
    //Compressed/encoded payloads are a single base64url token, possibly
    //split across lines by the save dialog's split option. Consume the
    //token run on the marker line, then any following all-token lines;
    //stop at blank lines, chat text, or trailing junk.
    const lines = rawText.substring(markerIndex).split(/\r?\n/);
    let token = '';
    for(let i = 0; i < lines.length; ++i) {
        if(i == 0) {
            //lines[0] starts exactly at the marker (substring cut)
            const run = /^[A-Za-z0-9_$-]+={0,2}/.exec(lines[i]);
            if(!run)
                return undefined;
            token = run[0];
            if(run[0].length < lines[i].length)
                break; //junk after the token on the same line — done
        } else {
            const line = lines[i].trim();
            if(!TOKEN_LINE.test(line))
                break;
            token += line;
        }
    }
    return token ? {encodedBlueprint: token, blueprintName} : undefined;
}
